import React, { useState } from "react";
import { Table, Form, Row, Col, Button } from "react-bootstrap";
import { CSVLink } from "react-csv";
import dummyInvoices from "./_dummy_invoices_data";

const InvoiceTable = () => {
  const [filter, setFilter] = useState({
    year: new Date().getFullYear(),
    periodType: "month", // 'month' or 'quarter'
    month: new Date().getMonth() + 1,
    quarter: Math.ceil((new Date().getMonth() + 1) / 3),
  });

  const [currentPage, setCurrentPage] = useState(1);
  const invoicesPerPage = 10;

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({
      ...prev,
      [name]: name === "year" || name === "month" || name === "quarter" ? parseInt(value) : value,
    }));
  };

  // Convert month number to name
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Define months for each quarter
  const quarterMonths = {
    1: [1, 2, 3],   // Q1: Jan - Mar
    2: [4, 5, 6],   // Q2: Apr - Jun
    3: [7, 8, 9],   // Q3: Jul - Sep
    4: [10, 11, 12] // Q4: Oct - Dec
  };

  // Filter invoices based on selected Year, Month, or Quarter
  const filteredInvoices = dummyInvoices.filter((invoice) => {
    const invoiceDate = new Date(invoice.invoiceDate);
    if (invoiceDate.getFullYear() !== filter.year) return false;

    if (filter.periodType === "month") {
      return invoiceDate.getMonth() + 1 === filter.month;
    } else {
      return quarterMonths[filter.quarter].includes(invoiceDate.getMonth() + 1);
    }
  });

  // Pagination Logic
  const indexOfLastInvoice = currentPage * invoicesPerPage;
  const indexOfFirstInvoice = indexOfLastInvoice - invoicesPerPage;
  const currentInvoices = filteredInvoices.slice(indexOfFirstInvoice, indexOfLastInvoice);

  // Pagination Controls
  const totalPages = Math.ceil(filteredInvoices.length / invoicesPerPage);

  // Prepare CSV Data
  const prepareCSVData = (invoices) => {
    return [
      ["Tenant Name", "House Number", "Rent (KSh)", "Tax (KSh)", "Total (KSh)", "Invoice Date"],
      ...invoices.map((invoice) => [
        invoice.tenant,
        invoice.houseNumber,
        invoice.rent,
        invoice.tax,
        invoice.rent + invoice.tax,
        invoice.invoiceDate,
      ]),
    ];
  };

  return (
    <div className="tasks mt-4">
      <h4 className="mb-3">Invoice Table</h4>

      {/* Filter Section */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Select name="year" value={filter.year} onChange={handleFilterChange}>
            {[2024, 2025].map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select name="periodType" value={filter.periodType} onChange={handleFilterChange}>
            <option value="month">Monthly</option>
            <option value="quarter">Quarterly</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          {filter.periodType === "month" ? (
            <Form.Select name="month" value={filter.month} onChange={handleFilterChange}>
              {monthNames.map((name, index) => (
                <option key={index + 1} value={index + 1}>{name}</option>
              ))}
            </Form.Select>
          ) : (
            <Form.Select name="quarter" value={filter.quarter} onChange={handleFilterChange}>
              {[1, 2, 3, 4].map((q) => (
                <option key={q} value={q}>Q{q}</option>
              ))}
            </Form.Select>
          )}
        </Col>
        <Col md={3} className="d-flex gap-2">
          <CSVLink
            data={prepareCSVData(filteredInvoices)}
            filename={`filtered-invoices-${filter.year}-${filter.periodType === "month" ? filter.month : `Q${filter.quarter}`}.csv`}
            className="btn btn-sm btn-outline-success"
            style={{height:"fit-content"}}
          >
            Export Filtered
          </CSVLink>
          <CSVLink
            data={prepareCSVData(dummyInvoices)}
            filename="all-invoices.csv"
            className="btn btn-sm btn-outline-warning"
            style={{height:"fit-content"}}

          >
            Export All
          </CSVLink>
        </Col>
      </Row>

      {/* Table Section */}
      <Table>
        <thead>
          <tr>
            <th>#</th>
            <th>Tenant Name</th>
            <th>House Number</th>
            <th>Rent (KSh)</th>
            <th>Tax (KSh)</th>
            <th>Total (KSh)</th>
            <th>Invoice Date</th>
          </tr>
        </thead>
        <tbody>
          {currentInvoices.length > 0 ? (
            currentInvoices.map((invoice, index) => (
              <tr key={invoice.id}>
                <td>{indexOfFirstInvoice + index + 1}</td>
                <td>{invoice.tenant}</td>
                <td>{invoice.houseNumber}</td>
                <td>KSH. {invoice.rent.toLocaleString()}</td>
                <td>KSH. {invoice.tax.toLocaleString()}</td>
                <td>KSH. {(invoice.rent + invoice.tax).toLocaleString()}</td>
                <td>{invoice.invoiceDate}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" className="text-center">No invoices found for selected period</td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center">
          <Button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Previous</Button>
          <span className="mx-3">Page {currentPage} of {totalPages}</span>
          <Button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
};

export default InvoiceTable;
